import React, { useState, useEffect, useRef } from 'react';
import './ClientFeeds.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const formatFileSize = (bytes) => {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
};

function CategoryMultiSelect({ categories, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const isAll = selectedIds.length === 0;
  const bearingIds = categories
    .filter((c) => Number(c.id) === 226 || c.name.trim().toLocaleLowerCase('ru-RU') === 'подшипники')
    .map((c) => Number(c.id));
  const withoutBearingIds = categories
    .map((c) => Number(c.id))
    .filter((id) => !bearingIds.includes(id));
  const isWithoutBearings = bearingIds.length > 0
    && selectedIds.length === withoutBearingIds.length
    && withoutBearingIds.every((id) => selectedIds.includes(id));

  const summary = isAll
    ? 'Весь каталог'
    : isWithoutBearings
      ? 'Весь ассортимент, кроме подшипников'
      : `Выбрано категорий: ${selectedIds.length}`;

  const toggleCategory = (id) => {
    const has = selectedIds.includes(id);
    onChange(has ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };

  return (
    <div className={`cf-multiselect${open ? ' cf-multiselect-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        className="cf-multiselect-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>{summary}</span>
        <svg className="cf-multiselect-chevron" width="10" height="6" viewBox="0 0 10 6" aria-hidden="true">
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="cf-multiselect-panel" role="listbox">
          <CategoryOption
            label="Весь каталог (все категории)"
            checked={isAll}
            onChange={() => onChange([])}
            strong
          />
          {bearingIds.length > 0 && (
            <CategoryOption
              label="Весь ассортимент, кроме подшипников"
              checked={isWithoutBearings}
              onChange={() => onChange(withoutBearingIds)}
              strong
            />
          )}
          <div className="cf-multiselect-divider" />
          <div className="cf-multiselect-list">
            {categories.map((c) => (
              <CategoryOption
                key={c.id}
                label={c.name.trim()}
                checked={selectedIds.includes(c.id)}
                onChange={() => toggleCategory(c.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CategoryOption({ label, checked, onChange, strong }) {
  const classes = ['cf-option'];
  if (strong) classes.push('cf-option-strong');
  if (checked) classes.push('cf-option-selected');
  return (
    <label className={classes.join(' ')}>
      <input type="checkbox" className="cf-option-input" checked={checked} onChange={onChange} />
      <span className="cf-option-label">{label}</span>
    </label>
  );
}

function ClientFeeds({ token }) {
  const [priceTypes, setPriceTypes] = useState([]);
  const [clientTypes, setClientTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [log, setLog] = useState([]);
  const [loadingLog, setLoadingLog] = useState(true);

  const [feedForm, setFeedForm] = useState({ company_name: '', price_type_id: '', section_ids: [] });
  const [feedResult, setFeedResult] = useState(null);
  const [feedError, setFeedError] = useState('');
  const [feedSubmitting, setFeedSubmitting] = useState(false);
  const [feedDocuments, setFeedDocuments] = useState([]);
  const feedDocumentInputRef = useRef(null);

  const [clientForm, setClientForm] = useState({ company_name: '', email: '', password: '', client_type: '' });
  const [clientResult, setClientResult] = useState(null);
  const [clientError, setClientError] = useState('');
  const [clientSubmitting, setClientSubmitting] = useState(false);

  const [copiedKey, setCopiedKey] = useState('');

  const copyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => (k === key ? '' : k)), 1500);
    } catch (e) {
      window.prompt('Скопируйте вручную (Ctrl+C):', text);
    }
  };

  const copyLogPassword = async (row) => {
    try {
      const resp = await fetch(`${API_URL}/api/ipk/log/${row.id}/password`, { headers: authHeaders });
      const data = await resp.json();
      if (!resp.ok) {
        window.alert(data.detail || 'Не удалось получить пароль');
        return;
      }
      await copyText(data.password, `log-${row.id}`);
    } catch (e) {
      window.alert('Сайт недоступен, попробуйте позже');
    }
  };

  const authHeaders = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
  const authOnlyHeaders = { 'Authorization': `Bearer ${token}` };

  const downloadDocument = async (document) => {
    try {
      const resp = await fetch(`${API_URL}/api/ipk/documents/${document.id}`, { headers: authOnlyHeaders });
      if (!resp.ok) {
        window.alert('Не удалось скачать документ');
        return;
      }
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = document.original_name;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      window.alert('Не удалось скачать документ');
    }
  };

  const loadLog = async () => {
    setLoadingLog(true);
    try {
      const resp = await fetch(`${API_URL}/api/ipk/log`, { headers: authHeaders });
      const data = await resp.json();
      setLog(data.entries || []);
    } catch (e) {
      // тихо игнорируем - журнал не критичен для работы формы
    } finally {
      setLoadingLog(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/ipk/price-types`, { headers: authHeaders })
      .then((r) => r.json())
      .then((d) => setPriceTypes(d.price_types || []))
      .catch(() => {});
    fetch(`${API_URL}/api/ipk/categories`, { headers: authHeaders })
      .then((r) => r.json())
      .then((d) => setCategories(d.categories || []))
      .catch(() => {});
    fetch(`${API_URL}/api/ipk/client-types`, { headers: authHeaders })
      .then((r) => r.json())
      .then((d) => setClientTypes(d.client_types || []))
      .catch(() => {});
    loadLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (loadingLog) return;
    const logId = new URLSearchParams(window.location.search).get('log_id');
    if (!logId) return;
    const row = window.document.getElementById(`provisioning-log-${logId}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [loadingLog, log]);

  const submitFeed = async (e) => {
    e.preventDefault();
    setFeedError('');
    setFeedResult(null);
    if (!feedForm.company_name.trim()) {
      setFeedError('Укажите название компании клиента');
      return;
    }
    if (!feedForm.price_type_id) {
      setFeedError('Выберите тип цены');
      return;
    }
    if (feedDocuments.length === 0) {
      setFeedError('Прикрепите скан дополнительного соглашения');
      return;
    }
    setFeedSubmitting(true);
    try {
      const body = new FormData();
      body.append('company_name', feedForm.company_name.trim());
      body.append('price_type_id', String(Number(feedForm.price_type_id)));
      body.append('section_ids', JSON.stringify(feedForm.section_ids));
      feedDocuments.forEach((file) => body.append('documents', file));
      const resp = await fetch(`${API_URL}/api/ipk/feeds`, {
        method: 'POST',
        headers: authOnlyHeaders,
        body,
      });
      const data = await resp.json();
      if (!resp.ok) {
        setFeedError(data.detail || 'Не удалось создать фид');
      } else {
        setFeedResult(data);
        setFeedForm({ company_name: '', price_type_id: '', section_ids: [] });
        setFeedDocuments([]);
        if (feedDocumentInputRef.current) feedDocumentInputRef.current.value = '';
        loadLog();
      }
    } catch (err) {
      setFeedError('Сайт ИПК недоступен, попробуйте позже');
    } finally {
      setFeedSubmitting(false);
    }
  };

  const submitClient = async (e) => {
    e.preventDefault();
    setClientError('');
    setClientResult(null);
    if (!clientForm.company_name.trim() || !clientForm.email.trim() || clientForm.password.length < 6) {
      setClientError('Заполните название компании, email и пароль (от 6 символов)');
      return;
    }
    if (!clientForm.client_type) {
      setClientError('Выберите тип клиента (Россия или СНГ)');
      return;
    }
    setClientSubmitting(true);
    try {
      const resp = await fetch(`${API_URL}/api/ipk/clients`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(clientForm),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setClientError(data.detail || 'Не удалось создать пользователя');
      } else {
        setClientResult({ ...data, password: clientForm.password });
        setClientForm({ company_name: '', email: '', password: '', client_type: '' });
      }
      loadLog();
    } catch (err) {
      setClientError('Сайт ИПК недоступен, попробуйте позже');
    } finally {
      setClientSubmitting(false);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let pwd = '';
    for (let i = 0; i < 10; i++) pwd += chars[Math.floor(Math.random() * chars.length)];
    setClientForm((f) => ({ ...f, password: pwd }));
  };

  const focusedLogId = Number(new URLSearchParams(window.location.search).get('log_id') || 0);

  return (
    <div className="client-feeds">
      <div className="settings-header">
        <h1>🔗 Клиентские фиды и доступы</h1>
        <p className="subtitle">
          Создание XML-прайса для клиента и регистрация его на сайте ipk-service.ru —
          без входа в админку Битрикса. Фид обновляется на сайте раз в сутки автоматически.
        </p>
      </div>

      <div className="cf-columns">
        <section className="cf-card">
          <h2>Создать XML-фид</h2>
          <form onSubmit={submitFeed}>
            <label>
              Название компании клиента
              <input
                type="text"
                required
                value={feedForm.company_name}
                onChange={(e) => setFeedForm((f) => ({ ...f, company_name: e.target.value }))}
                placeholder="ООО «Ромашка»"
              />
            </label>

            <label>
              Тип цены
              <select
                required
                value={feedForm.price_type_id}
                onChange={(e) => setFeedForm((f) => ({ ...f, price_type_id: e.target.value }))}
              >
                <option value="">— выберите —</option>
                {priceTypes.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </label>

            <div className="cf-field">
              <span>Категории товара</span>
              <CategoryMultiSelect
                categories={categories}
                selectedIds={feedForm.section_ids}
                onChange={(ids) => setFeedForm((f) => ({ ...f, section_ids: ids }))}
              />
            </div>

            <label>
              Скан дополнительного соглашения
              <input
                ref={feedDocumentInputRef}
                type="file"
                required
                multiple
                accept=".pdf,.jpg,.jpeg,.png,.gif,.tif,.tiff,.webp,.heic,.heif,.bmp"
                onChange={(e) => setFeedDocuments(Array.from(e.target.files || []))}
              />
              <span className="cf-file-help">PDF или изображения, можно выбрать несколько файлов</span>
            </label>
            {feedDocuments.length > 0 && (
              <div className="cf-selected-files">
                {feedDocuments.map((file, index) => (
                  <div className="cf-selected-file" key={`${file.name}-${file.size}-${index}`}>
                    <span title={file.name}>{file.name}</span>
                    <small>{formatFileSize(file.size)}</small>
                    <button
                      type="button"
                      aria-label={`Убрать ${file.name}`}
                      onClick={() => setFeedDocuments((files) => files.filter((_, i) => i !== index))}
                    >×</button>
                  </div>
                ))}
              </div>
            )}

            {feedError && <div className="cf-error">{feedError}</div>}
            {feedResult && (
              <div className="cf-success">
                Готово{feedResult.reused_existing ? ' (такой фид уже существовал — выдана та же ссылка)' : ''}:
                <br />
                <a href={feedResult.feed_url} target="_blank" rel="noreferrer">{feedResult.feed_url}</a>
                {feedResult.notification_sent === false && (
                  <div className="cf-notification-warning">Фид создан, но почтовое уведомление отправить не удалось.</div>
                )}
              </div>
            )}

            <button type="submit" disabled={feedSubmitting}>
              {feedSubmitting ? 'Создаю...' : 'Создать фид'}
            </button>
          </form>
        </section>

        <section className="cf-card">
          <h2>Зарегистрировать клиента на сайте</h2>
          <p className="cf-hint">Тип клиента определяет группу и цены, которые он увидит на сайте.</p>
          <form onSubmit={submitClient}>
            <label>
              Тип клиента
              <select
                required
                value={clientForm.client_type}
                onChange={(e) => setClientForm((f) => ({ ...f, client_type: e.target.value }))}
              >
                <option value="">— выберите —</option>
                {clientTypes.map((c) => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </label>
            <label>
              Название компании
              <input
                type="text"
                required
                value={clientForm.company_name}
                onChange={(e) => setClientForm((f) => ({ ...f, company_name: e.target.value }))}
                placeholder="ООО «Ромашка»"
              />
            </label>
            <label>
              Email (он же логин)
              <input
                type="email"
                required
                value={clientForm.email}
                onChange={(e) => setClientForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="client@example.com"
              />
            </label>
            <label>
              Пароль
              <div className="cf-password-row">
                <input
                  type="text"
                  required
                  minLength={6}
                  value={clientForm.password}
                  onChange={(e) => setClientForm((f) => ({ ...f, password: e.target.value }))}
                />
                <button type="button" onClick={generatePassword}>Сгенерировать</button>
              </div>
            </label>

            {clientError && <div className="cf-error">{clientError}</div>}
            {clientResult && (
              <div className="cf-success cf-success-with-action">
                <span>{clientResult.status === 'updated' ? 'Группа добавлена, пароль обновлён' : 'Пользователь создан'}: {clientResult.login} ({clientResult.group})</span>
                <button
                  type="button"
                  className="cf-copy-btn"
                  onClick={() => copyText(clientResult.password, 'new-client')}
                >
                  {copiedKey === 'new-client' ? 'Скопировано' : 'Скопировать пароль'}
                </button>
                {clientResult.notification_sent === false && (
                  <div className="cf-notification-warning">Доступ сохранён, но почтовое уведомление отправить не удалось.</div>
                )}
              </div>
            )}

            <button type="submit" disabled={clientSubmitting}>
              {clientSubmitting ? 'Создаю...' : 'Зарегистрировать'}
            </button>
          </form>
        </section>
      </div>

      <section className="cf-card cf-log" id="provisioning-log">
        <h2>Журнал (последние действия)</h2>
        {loadingLog ? (
          <div className="loading">Загрузка...</div>
        ) : log.length === 0 ? (
          <p className="cf-hint">Пока ничего не создавалось.</p>
        ) : (
          <table className="cf-log-table">
            <thead>
              <tr>
                <th>Когда</th>
                <th>Что</th>
                <th>Компания</th>
                <th>Менеджер</th>
                <th>Результат</th>
                <th>Документы</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {log.map((row) => (
                <tr
                  id={`provisioning-log-${row.id}`}
                  key={`${row.action}-${row.id}`}
                  className={`${row.result !== 'ok' ? 'cf-row-error' : ''}${focusedLogId === row.id ? ' cf-row-focused' : ''}`}
                >
                  <td>{row.created_at ? new Date(row.created_at).toLocaleString('ru-RU') : '—'}</td>
                  <td>
                    {row.action === 'feed' ? 'Фид' : 'Клиент'}
                    {row.client_type && ` (${row.client_type === 'russia' ? 'Россия' : 'СНГ'})`}
                  </td>
                  <td>{row.company_name}</td>
                  <td>{row.manager_email || '—'}</td>
                  <td>
                    {row.result === 'ok'
                      ? (row.feed_url
                          ? <a href={row.feed_url} target="_blank" rel="noreferrer">ссылка</a>
                          : (row.bitrix_login || 'ок'))
                      : `ошибка: ${row.error_message || ''}`}
                  </td>
                  <td>
                    {(row.documents || []).length > 0 ? (
                      <div className="cf-documents">
                        {row.documents.map((document) => (
                          <button
                            type="button"
                            className="cf-document-link"
                            key={document.id}
                            title={`${document.original_name} (${formatFileSize(document.size_bytes)})`}
                            onClick={() => downloadDocument(document)}
                          >
                            {document.original_name}
                          </button>
                        ))}
                      </div>
                    ) : '—'}
                  </td>
                  <td>
                    {row.has_password && (
                      <button type="button" className="cf-copy-btn cf-copy-btn-small" onClick={() => copyLogPassword(row)}>
                        {copiedKey === `log-${row.id}` ? 'Скопировано' : 'Скопировать пароль'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

export default ClientFeeds;
